import { cn } from '@/lib/utils'
import type { PanelArtProps } from './panel-art-props'
import { ANCHOR_X, HUB_TAPE, linePath } from './chart-candles'

const BODY_W = HUB_TAPE.pitch * 0.6

/**
 * History Chart panel art: the shared candle tape anchored on a past day. A
 * dashed accent marker runs the full height at day zero, and one muted
 * average runs underneath.
 */
export function HistoryChartArt({ className }: PanelArtProps) {
  return (
    <svg
      viewBox="0 0 160 100"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
      className={cn('h-full w-full', className)}
    >
      {/* Day zero marker, drawn first so the candles sit on top of it. */}
      <path
        d={`M${ANCHOR_X} 12 V88`}
        fill="none"
        stroke="hsl(var(--primary) / 0.8)"
        strokeWidth="1.5"
        strokeDasharray="3 4"
        vectorEffect="non-scaling-stroke"
      />
      {/* One muted average under the tape. */}
      <path
        d={linePath(HUB_TAPE.average)}
        fill="none"
        stroke="hsl(var(--muted-foreground) / 0.55)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {HUB_TAPE.candles.map(([cx, top, bottom, high, low, rising]) => {
        const tone = rising ? 'hsl(var(--chart-candle-up))' : 'hsl(var(--chart-candle-down))'
        return (
          <g key={cx}>
            <path
              d={`M${cx} ${high} V${low}`}
              stroke={tone}
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
            {/* Square bodies with a full-tone outline: crisp, strong edges. */}
            <rect
              x={cx - BODY_W / 2}
              y={top}
              width={BODY_W}
              height={Math.max(bottom - top, 1)}
              fill={tone}
              stroke={tone}
              strokeWidth="0.5"
            />
          </g>
        )
      })}
    </svg>
  )
}
